/**
 * Shared formatting for job results in MCP tool responses.
 * Used by both get_job and wait tools to avoid duplicated formatting logic.
 */

import type { JobStatusResponse, PassStatus, RecordingStatus, VerdictConfidence } from '@runhuman/shared';
import { SOURCE_URL_LABEL } from '@runhuman/shared';
import { formatTesterPool } from './format-tester-pool.js';

const PASS_STATUS_LABEL: Record<PassStatus, string> = {
  pass: 'Pass',
  fail: 'Fail',
  not_applicable: 'Not applicable',
};

function formatPassStatusLine(passStatus: PassStatus | null | undefined): string {
  if (!passStatus) return '';
  return `\nPass/Fail: ${PASS_STATUS_LABEL[passStatus]} (${passStatus})`;
}

/**
 * Surfaces the deterministic consistency gate's signal (#6008): a LOW verdict
 * means the grader's verdict contradicted its own evidence (transcript /
 * extracted issues), so the calling agent should verify against the recording
 * and artifacts before acting on it.
 */
function formatVerdictConfidenceLine(confidence: VerdictConfidence | undefined): string {
  if (!confidence || confidence.level === 'high') return '';
  const reasons = confidence.flags.map((flag) => `\n  - ${flag.detail}`).join('');
  return `\nVerdict confidence: LOW — the verdict contradicts its own evidence; verify against the recording and artifacts before acting on it.${reasons}`;
}

/**
 * Whether a job's screen recording is fetchable, without minting a presigned URL
 * on every poll. Lets an agent decide whether calling
 * `get_test_artifacts(artifacts: ["recording"])` will return anything yet (#5943).
 */
const RECORDING_STATUS_HINT: Record<RecordingStatus, string> = {
  ready: 'ready — fetch it with get_test_artifacts(artifacts: ["recording"])',
  processing: 'processing — not ready yet, poll get_job again shortly',
  stripping_audio: 'processing (audio-privacy strip in progress) — not ready yet',
  strip_failed: 'unavailable — the audio-privacy strip failed',
  none: 'none — this job has no screen recording',
};

function formatRecordingStatusLine(status: RecordingStatus | undefined): string {
  if (!status) return '';
  return `\nRecording: ${RECORDING_STATUS_HINT[status]}`;
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatExtractedIssues(job: JobStatusResponse): string {
  if (!job.extractedIssues?.length) return '';

  const issueLines = job.extractedIssues.map((issue, i) => {
    const lines = [
      `  ${i + 1}. [${issue.severity.toUpperCase()}] ${issue.title}`,
      `     ${issue.description}`,
    ];
    // The finding's "where" — the human-readable region and the structured,
    // telemetry-resolved page URL (#6094) — which the MCP output dropped before.
    if (issue.affectedArea) lines.push(`     Where: ${issue.affectedArea}`);
    if (issue.sourceUrl) lines.push(`     ${SOURCE_URL_LABEL}: ${issue.sourceUrl}`);
    if (issue.relatedIssues?.length) {
      const related = issue.relatedIssues
        .map(r => `#${r.issueNumber} (${r.relation}, ${Math.round(r.confidence * 100)}%)`)
        .join(', ');
      lines.push(`     Related: ${related}`);
    }
    return lines.join('\n');
  });

  return `\n\nExtracted Issues (${job.extractedIssues.length}):\n${issueLines.join('\n')}`;
}

function formatCreatedIssues(job: JobStatusResponse): string {
  if (!job.createdIssues?.length) return '';

  const issueLines = job.createdIssues.map(issue => {
    const status = issue.isNew ? 'new issue' : 'comment on existing';
    const matchInfo = issue.matchReason ? ` — ${issue.matchReason}` : '';
    return `  - #${issue.issueNumber}: ${issue.title} (${status}${matchInfo})\n    ${issue.issueUrl}`;
  });

  return `\n\nCreated GitHub Issues (${job.createdIssues.length}):\n${issueLines.join('\n')}`;
}

/**
 * Formats a completed job into a human-readable string for MCP tool responses.
 */
export function formatCompletedJob(jobId: string, job: JobStatusResponse): string {
  let resultsText = '';
  if (job.result) {
    resultsText = '\n\nResults:\n' + Object.entries(job.result)
      // verdictConfidence renders as its own labeled line, not a raw object dump.
      .filter(([key]) => key !== 'verdictConfidence')
      .map(([key, value]) => `  ${key}: ${value}`)
      .join('\n');
  }

  let testerInfo = '';
  if (job.testerAlias) {
    testerInfo = `\nTester: ${job.testerAlias}`;
  }

  const testerPoolInfo = formatTesterPool(job);
  const extractedIssuesText = formatExtractedIssues(job);
  const createdIssuesText = formatCreatedIssues(job);
  const passStatusLine = formatPassStatusLine(job.passStatus);
  const confidenceLine = formatVerdictConfidenceLine(job.result?.verdictConfidence);
  const recordingLine = formatRecordingStatusLine(job.recordingStatus);

  return `Job Completed

Job ID: ${jobId}
Status: completed${passStatusLine}${confidenceLine}
Duration: ${job.testDurationSeconds ? `${job.testDurationSeconds}s` : 'N/A'}
Cost: $${(job.costUsd || 0).toFixed(3)}${testerInfo}${testerPoolInfo}${recordingLine}${resultsText}${extractedIssuesText}${createdIssuesText}

Raw Response:
${job.testerResponse ? job.testerResponse : 'No response text'}

${job.jobUrl ? `View in dashboard: ${job.jobUrl}` : ''}

Tip: Use get_test_artifacts for detailed console logs, transcript, network requests, and more.`;
}

/**
 * Formats a job that ended in a non-completed terminal state
 * (cancelled / expired / failed) for MCP tool responses.
 */
export function formatTerminalFailure(jobId: string, job: JobStatusResponse): string {
  const label = titleCase(job.status);
  const reasonLine = job.modifier ? `\nReason: ${job.modifier}` : '';
  const detailsLine = job.testerResponse
    ? `\nDetails: ${job.testerResponse}`
    : '\nDetails: No details available';
  const dashboardLine = job.jobUrl ? `\n\nView in dashboard: ${job.jobUrl}` : '';

  return `Job ${label}

Job ID: ${jobId}
Status: ${job.status}${reasonLine}${detailsLine}${dashboardLine}`;
}

/**
 * True iff the job is completed AND the AI verdict is explicitly `fail`.
 * Drives `isError` on wait-tool responses so calling agents see CI-style
 * failures even when the underlying job didn't error.
 */
export function isFailedVerdict(job: JobStatusResponse): boolean {
  return job.status === 'completed' && job.passStatus === 'fail';
}
